import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  Optional,
  Self,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { SgFormControlBase, subscriptAnimation } from '../../shared';
import { sanitizeDecimalPlaces } from './number-precision.util';

@Component({
  selector: 'sg-input-2',
  templateUrl: './sg-input-2.component.html',
  styleUrls: ['./sg-input-2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [subscriptAnimation],
  standalone: false,
})
export class SgInput2Component extends SgFormControlBase<string> implements AfterViewInit, OnChanges {
  @ViewChild('inputElement', { static: true }) inputElement!: ElementRef<HTMLInputElement>;
  @ViewChild('containerElement', { static: true }) containerElement!: ElementRef<HTMLDivElement>;
  @ViewChild('prefixTextElement') prefixTextElement?: ElementRef<HTMLSpanElement>;

  @Input() placeholder: string = '';
  @Input() type: string = 'text';
  @Input() size: 'default' | 'small' = 'default';
  @Input() variant: 'default' | 'secondary' | 'underline' | 'pill' = 'default';
  @Input() prefixText: string = '';
  @Input() prefixIconSize: string = '24px';
  @Input() suffixIcon: string = '';
  @Input() suffixText: string = '';
  @Input() submitIcon: string = '';
  @Input() step: number | string | null = null;
  @Input() min: number | string | null = null;
  @Input() max: number | string | null = null;
  /** Helper text shown in a tooltip on an info (ⓘ) icon next to the label. The icon only renders when set. */
  @Input() labelTooltip = '';
  /** Optional bold heading shown above the tooltip helper text. */
  @Input() labelTooltipHeading = '';

  /**
   * Maximum decimal places for number inputs. Only applies when type="number".
   * - `null` (default): No restriction
   * - `0`: Integers only
   * - `1+`: Allows up to N decimal places
   */
  @Input() precision: number | null = null;

  @Output() readonly valueChange = new EventEmitter<string>();
  @Output() readonly submit = new EventEmitter<string>();

  constructor(@Optional() @Self() override ngControl: NgControl | null) {
    super(ngControl);
  }

  ngAfterViewInit(): void {
    this.updatePrefixTextOffset();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['prefixText'] || changes['size']) {
      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => this.updatePrefixTextOffset());
    }
  }

  private updatePrefixTextOffset(): void {
    if (this.prefixText && !this.prefixIcon && this.prefixTextElement) {
      const prefixWidth = this.prefixTextElement.nativeElement.offsetWidth;
      const gap = 4; // $sg-field-icon-gap
      this.containerElement.nativeElement.style.setProperty('--icon-offset', `${prefixWidth + gap}px`);
    } else if (!this.prefixIcon) {
      this.containerElement.nativeElement.style.removeProperty('--icon-offset');
    }
  }

  get empty(): boolean {
    return !this.value;
  }

  /**
   * Precision is applied to the emitted VALUE but never written back to the field while typing.
   * Assigning `input.value` moves the caret to the end (HTML Standard, value IDL setter) and a
   * number input cannot restore it — `setSelectionRange` throws. Rewriting here made a field already
   * at max precision swallow every keystroke after the first, so editing 0.35 towards 0.15 stuck at
   * 0.13. `onBlur` reconciles the field, by which point the caret no longer matters.
   */
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    this.value = this.applyPrecision(input.value);
    this.onChange(this.value);
    this.valueChange.emit(this.value);
  }

  /** Reconciles the field with the value precision already trimmed. Safe here: focus is leaving. */
  override onBlur(): void {
    super.onBlur();
    if (this.shouldEnforcePrecision && this.inputElement && this.inputElement.nativeElement.value !== this.value) {
      this.inputElement.nativeElement.value = this.value;
    }
  }

  private applyPrecision(rawValue: string): string {
    if (!this.shouldEnforcePrecision) {
      return rawValue;
    }

    return sanitizeDecimalPlaces(rawValue, this.precision!) ?? rawValue;
  }

  focusInput(): void {
    this.inputElement.nativeElement.focus();
  }

  onContainerClick(event: MouseEvent): void {
    if (this.disabled) {
      return;
    }

    const target = event.target as HTMLElement;
    if (target.tagName !== 'INPUT') {
      this.focusInput();
    }
  }

  clear(): void {
    this.value = '';
    this.inputElement.nativeElement.value = '';
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.focusInput();
    this.cdr.markForCheck();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.submit.emit(this.value);
    }

    if (this.shouldEnforcePrecision) {
      this.enforcePrecisionOnKeydown(event);
    }
  }

  onSubmitClick(): void {
    this.submit.emit(this.value);
  }

  writeValue(value: string | null): void {
    // Precision applies to programmatic writes too — this is not typing, so there is no caret to
    // protect. Without it a [precision]="2" field displayed the drawer's 4-decimal bpsToPercent
    // prefill (0.125), a value the user is not allowed to type and which collapsed on first edit.
    // `|| ''` would blank a legitimate 0, which [ngModel] delivers as a number.
    this._value = this.applyPrecision(value == null ? '' : String(value));
    if (this.inputElement) {
      this.inputElement.nativeElement.value = this._value;
    }
    this.cdr.markForCheck();
  }

  /** Whether precision enforcement is active (type="number" with precision set) */
  private get shouldEnforcePrecision(): boolean {
    return this.type === 'number' && this.precision !== null;
  }

  // Separators only. Excess digits can't be judged from a keystroke — a number input reports
  // `selectionStart` as null, so there is no telling which side of the separator one lands on.
  // `onInput` trims the resulting value instead.
  private enforcePrecisionOnKeydown(event: KeyboardEvent): void {
    if (!this.isDecimalSeparator(event.key)) {
      return;
    }

    const hasDecimal = this.getDecimalIndex(this.inputElement.nativeElement.value) !== -1;
    if (this.precision === 0 || hasDecimal) {
      event.preventDefault();
    }
  }

  private isDecimalSeparator(char: string): boolean {
    return char === '.' || char === ',';
  }

  private getDecimalIndex(value: string): number {
    return Math.max(value.indexOf('.'), value.indexOf(','));
  }
}
